/**
 * The value of an origin or destination box: what the user picked, and the
 * airports and ports found near it. Every ticked mode searches its own kind of
 * site, so one pick feeds Flight (airports), Ocean (ports) and Road (address).
 */
import type {
  LaneMode,
  LanePlace,
  LaneSite,
  LaneSites,
  ResolvedSites,
} from '../../../apis/lanePlanner';

export interface PlaceValue {
  /** What the box shows. */
  label: string;
  /** A city or address, or one airport or port picked by name or code. */
  kind?: 'place' | 'airport' | 'port';
  /** The street address a Road search uses, when it differs from the label (a saved location's name). */
  address?: string;
  lat?: number;
  lng?: number;
  /** Airports and ports near the pick, nearest first; `default` ones are searched. */
  airports: LaneSite[];
  ports: LaneSite[];
  /** The nearby sites are still being looked up. */
  resolving?: boolean;
  /** Why the nearby sites could not be looked up. */
  error?: string;
}

/** The longest street address the search API accepts as a place code. */
const MAX_ADDRESS_LENGTH = 200;

export const placeValueFromResolved = (
  label: string,
  resolved: ResolvedSites,
  at?: { lat: number; lng: number },
  kind: PlaceValue['kind'] = 'place',
  address?: string
): PlaceValue => ({
  label,
  kind,
  ...(address ? { address } : {}),
  lat: at?.lat ?? resolved.point.lat,
  lng: at?.lng ?? resolved.point.lng,
  airports: resolved.airports,
  ports: resolved.ports,
});

/** How near a Google airport place must be to a bundled airport to count as that airport. */
const SNAP_KM = 5;

/** How far from the place an airport named in its label may sit (terminals are spread out). */
const NAMED_KM = 30;

/** The airport code a Google label names, as in "John F. Kennedy International Airport (JFK)". */
export const airportCodeInLabel = (label: string): string | null =>
  /\(([A-Z]{3})\)/.exec(label)?.[1] ?? null;

/**
 * A Google place that is an airport is one airport, like a pick by code. The
 * airport its label names becomes the only one searched; failing that, the
 * nearest bundled airport when Google files the place as one (`flagged`).
 * Null when neither applies (the place is then treated as any other place).
 */
export const snapToAirport = (
  resolved: ResolvedSites,
  { code, flagged }: { code: string | null; flagged: boolean }
): ResolvedSites | null => {
  const target =
    resolved.airports.find(
      (site) => site.code === code && site.distance_km <= NAMED_KM
    ) ??
    (flagged
      ? resolved.airports.find((site) => site.distance_km <= SNAP_KM)
      : undefined);
  if (!target) return null;
  return {
    ...resolved,
    airports: resolved.airports.map((site) => ({
      ...site,
      default: site.code === target.code,
    })),
  };
};

const searched = (sites: LaneSite[]): string[] =>
  sites.filter((site) => site.default).map((site) => site.code);

/** Why this pick cannot be searched in the ticked modes, or null when it can. */
export const placeProblem = (
  value: PlaceValue | undefined,
  modes: LaneMode[]
): string | null => {
  if (!value || !value.label.trim()) return 'Pick a place from the list.';
  if (value.resolving) return 'Still finding nearby airports and ports…';
  if (value.error) return value.error;
  if (modes.includes('road') && value.kind && value.kind !== 'place') {
    return 'Road needs a city or address. Pick one, or untick Road.';
  }
  const missing = (
    kind: 'airport' | 'port',
    sites: LaneSite[]
  ): string | null =>
    searched(sites).length > 0
      ? null
      : value.lat === undefined && value.kind !== kind
        ? // a place restored from an older search, never looked up
          `Pick ${value.label} again so we can find nearby ${kind}s.`
        : `No ${kind} found near ${value.label}. Untick ${kind === 'airport' ? 'Flight' : 'Ocean'} or pick another place.`;
  if (modes.includes('air')) {
    const problem = missing('airport', value.airports);
    if (problem) return problem;
  }
  if (modes.includes('ocean')) {
    const problem = missing('port', value.ports);
    if (problem) return problem;
  }
  return null;
};

/**
 * Why pickup or delivery trucking cannot be offered at this end, or null when it
 * can: trucking runs between a street address or city and the airport or port, so
 * the end must be a place (not an airport or port picked by name) in the US.
 * An unknown country (a place restored from a saved search) is not held against it.
 */
export const truckingProblem = (
  value: PlaceValue | undefined,
  modes: LaneMode[]
): string | null => {
  if (!value || !value.label.trim()) return 'Pick a place first.';
  if (value.kind && value.kind !== 'place') {
    return 'Trucking needs an address or city, not an airport or port.';
  }
  // A place restored from a saved search was never looked up, and an airport or
  // port picked by name restores as a labelled place, so without an address it
  // cannot be told from a city: ask for the pick again.
  if (!value.resolving && value.lat === undefined && !value.address) {
    return 'Pick this place again to add trucking.';
  }
  const sites = [
    ...(modes.includes('air') ? searched(value.airports) : []),
    ...(modes.includes('ocean') ? searched(value.ports) : []),
  ];
  const all = [...value.airports, ...value.ports];
  const outside =
    sites.length > 0 &&
    sites.every((code) => {
      const country = all.find((site) => site.code === code)?.country;
      return !!country && country !== 'US';
    });
  return outside ? 'Trucking is only quoted within the US.' : null;
};

/**
 * The place and per-kind candidate sites a request carries for the ticked modes.
 * `trucking` also sends the address, which pickup or delivery trucking prices from.
 */
export const requestPlaceOf = (
  value: PlaceValue,
  modes: LaneMode[],
  trucking = false
): { place: LanePlace; sites: LaneSites } => {
  const codes: LanePlace['codes'] = {};
  const sites: LaneSites = {};
  if (modes.includes('air')) {
    const airports = searched(value.airports);
    if (airports.length) {
      codes.airport = airports[0];
      sites.airport = airports;
    }
  }
  if (modes.includes('ocean')) {
    const ports = searched(value.ports);
    if (ports.length) {
      codes.port = ports[0];
      sites.port = ports;
    }
  }
  if (
    (modes.includes('road') || trucking) &&
    (!value.kind || value.kind === 'place')
  ) {
    codes.address = (value.address ?? value.label)
      .trim()
      .slice(0, MAX_ADDRESS_LENGTH);
  }
  return { place: { label: value.label, codes }, sites };
};

const siteFromCode = (code: string): LaneSite => ({
  code,
  name: code,
  city: '',
  country: '',
  distance_km: 0,
  default: true,
});

/** Reverse of `requestPlaceOf`: a stored place (new or older single-code shape) -> a box value. */
export const placeValueFromRequest = (
  place: LanePlace,
  sites: LaneSites | undefined
): PlaceValue => {
  const airports = sites?.airport?.length
    ? sites.airport
    : place.codes.airport
      ? [place.codes.airport]
      : [];
  const ports = sites?.port?.length
    ? sites.port
    : place.codes.port
      ? [place.codes.port]
      : [];
  const named = place.label ?? place.codes.address ?? place.codes.city;
  return {
    label: named ?? airports[0] ?? ports[0] ?? '',
    kind: named ? 'place' : airports[0] ? 'airport' : 'port',
    ...(place.codes.address ? { address: place.codes.address } : {}),
    airports: airports.map(siteFromCode),
    ports: ports.map(siteFromCode),
  };
};
